import React, { useState, useEffect, useRef } from 'react';
import '../styles/midnight-lab.css';

function FTCHero() {
  const [visible, setVisible] = useState(false);
  const [countValues, setCountValues] = useState({ seasons: 0, robots: 0, awards: 0 });
  const sectionRef = useRef(null);

  useEffect(() => {
    let intervalId = null;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !visible) {
          setVisible(true);
          
          // Start count animation
          const targets = { seasons: 5, robots: 12, awards: 15 };
          const duration = 2000;
          const steps = 60;
          const stepDuration = duration / steps;

          let currentStep = 0;
          intervalId = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;
            const easeOut = 1 - Math.pow(1 - progress, 3);

            setCountValues({
              seasons: Math.floor(targets.seasons * easeOut),
              robots: Math.floor(targets.robots * easeOut),
              awards: Math.floor(targets.awards * easeOut)
            });

            if (currentStep >= steps) {
              clearInterval(intervalId);
              setCountValues(targets);
            }
          }, stepDuration);
        }
      },
      { threshold: 0.2 }
    );

    const section = sectionRef.current;
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (section) {
        observer.unobserve(section);
      }
    };
  }, [visible]);

  return (
    <section 
      ref={sectionRef}
      style={{
        position: 'relative',
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-24) var(--space-4)',
        paddingTop: '100px',
        overflow: 'hidden',
        backgroundColor: 'var(--color-bg-primary)'
      }}
    >

      <div style={{
        position: 'relative',
        zIndex: 2,
        maxWidth: '1200px',
        width: '100%',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-block',
          padding: 'var(--space-2) var(--space-6)',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: '0',
          border: '1px solid rgba(0, 16, 17, 0.1)',
          marginBottom: 'var(--space-6)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}>
          FIRST Tech Challenge
        </div>

        <h1 className="heading-1" style={{
          marginBottom: 'var(--space-6)',
          color: 'var(--color-text-primary)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.98)',
          filter: visible ? 'blur(0px)' : 'blur(10px)',
          transition: 'opacity 0.8s ease-out 0.2s, transform 0.8s ease-out 0.2s, filter 0.8s ease-out 0.2s'
        }}>
          FTC Division
        </h1>

        <p style={{
          fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
          marginBottom: 'var(--space-8)',
          fontWeight: 300,
          lineHeight: 1.7,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-sans)',
          maxWidth: '800px',
          margin: '0 auto var(--space-8)',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease-out 0.4s, transform 0.6s ease-out 0.4s'
        }}>
          Competing at the highest level with 18" robots. Building innovative solutions, 
          mastering game strategy, and inspiring the next generation of engineers.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-6)',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease-out 0.6s, transform 0.6s ease-out 0.6s'
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.seasons}+
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Seasons
            </div>
          </div>
          <div style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease-out 0.7s, transform 0.6s ease-out 0.7s'
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.robots}
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Robots Built
            </div>
          </div>
          <div style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease-out 0.8s, transform 0.6s ease-out 0.8s'
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.awards}+
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Awards
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FTCHero;

